<template>
  <div>
    <v-row justify="space-between" v-if="search">
      <v-col cols="12" xl="3" lg="3" md="3" sm="4" xs="12">
        <SearchText
          :text="'Search by title'"
          :value="'title'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col cols="auto">
        <v-btn
          outlined
          class="black--text font-weight-bold mb-2"
          rounded
          large
          :to="{ name: 'CreateNewsAndEvents' }"
        >
          <v-icon color="black" small class="mr-2">
            $vuetify.icons.plus
          </v-icon>
          Create
        </v-btn>
      </v-col>
    </v-row>

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      class="mt-5"
      :items="items"
      :options.sync="options"
      :server-items-length="totalItems"
      id="news-events-values"
    >
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <!-- Edit -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="
                  () => {
                    itemForEdit = item;
                    getNewsAndEventsForEdit(item);
                  }
                "
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <!-- Delete -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="
                  () => {
                    itemToBeDeleted = item;
                    deleteDialog = true;
                  }
                "
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            {{ $t('delete.message') }}
          </v-tooltip>
          <!-- Copy -->
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="copyNewsEvents(item)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-content-copy
                </v-icon>
              </v-btn>
            </template>
            {{ $t('copy-message') }}
          </v-tooltip>
        </v-layout>
      </template>
      <template v-slot:item.title="{ item }">
        <div>
          {{ item.title }}
        </div>
      </template>
      <template v-slot:item.status="{ item }">
        <div>
          {{ item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase() }}
        </div>
      </template>
      <template v-slot:item.type="{ item }">
        <div>
          {{ item.type.charAt(0).toUpperCase() + item.type.slice(1).toLowerCase() }}
        </div>
      </template>
      <template v-slot:item.scientificDomain="{ item }">
        <div>
          <div
            v-for="(domain, index) in getUniqueDomains(item.scientifics).slice(0, 1)"
            :key="index"
          >
            <v-chip class="my-1" :style="{ backgroundColor: 'rgba(237, 224, 255, 1)' }">
              {{ domain }}
            </v-chip>
            <v-chip
              v-if="getUniqueDomains(item.scientifics).length > 1"
              class="ma-1"
              color="primary200"
            >
              +{{ getUniqueDomains(item.scientifics).length - 1 }} more
            </v-chip>
          </div>
        </div>
      </template>
      <template v-slot:item.subdomain="{ item }">
        <div>
          <div v-for="(type, index) in item.scientifics.slice(0, 1)" :key="index">
            <v-chip class="my-1" :style="{ backgroundColor: 'rgba(237, 224, 255, 1)' }">
              {{ type.subDomain }}
            </v-chip>
            <v-chip v-if="item.scientifics.length > 1" class="ma-1" color="primary200">
              +{{ item.scientifics.length - 1 }} more
            </v-chip>
          </div>
        </div>
      </template>
      <template v-slot:item.date="{ item }">
        <div v-if="item.type === 'NEWS'">
          {{ formatDateString(item.date) }}
        </div>
        <div v-else>
          <div v-if="item.date">
            {{ formatDateString(item.date) }}
          </div>
          <div v-else>
            {{ formatDateString(item.dateFrom) }} - {{ formatDateString(item.dateTo) }}
          </div>
        </div>
      </template>
    </v-data-table>

    <!-- Delete Dialog -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="1000px">
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="justify-center d-flex">
              <img
                alt="Logo"
                width="180"
                class="rounded-0 text-center"
                :src="require('@/assets/svg/logo.svg')"
              />
            </v-col>
            <v-col cols="1" class="pl-0">
              <v-btn fab small text @click="deleteDialog = false">
                <v-icon color="neutral500">
                  mdi-close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="custom-divider mt-10"></v-divider>

        <v-card-text class="justify-center d-flex mt-2">
          <v-icon size="150" color="black">$vuetify.icons.errorCirle</v-icon>
        </v-card-text>
        <v-card-text
          class="justify-center d-flex mt-2 black--text font-weight-bold"
          style="font-size: 20px;"
        >
          <div>
            Delete this
            <span class="primary200--text">
              {{ itemToBeDeleted.title }}
            </span>
            ?
          </div>
        </v-card-text>
        <v-card-text class="text-center" v-if="itemToBeDeleted.status === 'PUBLISHED'">
          This
          <span class="font-weight-bold primary200--text">
            {{
              itemToBeDeleted.type.charAt(0).toUpperCase() +
                itemToBeDeleted.type.slice(1).toLowerCase()
            }}
          </span>
          item is Published.
        </v-card-text>
        <v-card-text class="justify-center d-flex mt-2">
          <div>
            You will not be able to recover it.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                @click="deleteDialog = false"
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="rgba(157, 87, 244, 1)"
                outlined
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                @click="deleteNewsEvents(itemToBeDeleted)"
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="rgba(157, 87, 244, 1)"
                >{{ $t('Delete') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import util from '@/mixins/util.js';
import SearchText from '@/components/shared/search/SearchText';
import EventBus from '@/helpers/event-bus.js';

export default {
  name: 'TableNewsAndEvents',
  mixins: [util],
  components: {
    SearchText,
  },
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
  },
  data: () => ({
    headers: [
      { text: '', value: 'actions', sortable: false },
      { text: 'Title', value: 'title' },
      { text: 'Status', value: 'status' },
      { text: 'Type', value: 'type' },
      { text: 'Scientific Domain', value: 'scientificDomain', sortable: false },
      { text: 'Subdomain', value: 'subdomain', sortable: false },
      { text: 'Date', value: 'date' },
    ],
    firstLoad: true,
    items: [],
    totalItems: 0,
    options: {},
    itemForEdit: null,
    deleteDialog: false,
    itemToBeDeleted: null,
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.$route.query[attr]) {
            this.$set(this.search, attr, this.$route.query[attr]);
          }
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?`, {
            params: this.getParams(),
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      if (this.searchAttributes) {
        for (let i = 0; i < this.searchAttributes.length; i += 1) {
          const attr = this.searchAttributes[i];
          if (this.search[attr]) {
            query[attr] = this.search[attr];
          }
        }
      }
      return query;
    },
    formatDateString(dateString) {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${day}/${month}/${year}`;
    },
    getNewsAndEventsForEdit(item) {
      if (item) {
        const id = item.id;
        this.axios
          .get(`api/v1/news-and-events/${id}`)
          .then(response => {
            console.log('Response Data:', response.data);

            this.$router.push({
              name: 'EditNewsAndEvents',
              params: { id },
              query: { edit: true },
            });
          })
          .catch(error => {
            console.error('Error fetching :', error);
          });
      }
    },
    getUniqueDomains(scientifics) {
      const uniqueDomains = [...new Set(scientifics.map(sci => sci.domain))];
      return uniqueDomains;
    },
    deleteNewsEvents(itemToBeDeleted) {
      const id = itemToBeDeleted.id;
      if (id) {
        EventBus.$emit('waiting', true);

        this.axios
          .delete(`api/v1/news-and-events/${id}`)
          .then(response => {
            console.log('Deleted successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Deleted Successfully',
              color: 'primary200',
            });
            this.deleteDialog = false;
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Error deleting:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
    copyNewsEvents(item) {
      if (item) {
        const id = item.id;
        EventBus.$emit('waiting', true);

        this.axios
          .post(`api/v1/news-and-events/copy?eventIdToCopy=${id}`)
          .then(response => {
            console.log('Copied successfully:', response.data);
            EventBus.$emit('snackbar', {
              text: 'Copied Successfully',
              color: 'primary200',
            });
            this.getDataFromApi().then(data => {
              this.items = data.items;
              this.totalItems = data.total;
            });
          })
          .catch(error => {
            console.error('Error copying:', error);
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      }
    },
  },
};
</script>
